import { ArticleType } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { DefaultCartItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/constants/DefaultCartItem';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { ArticleTypeMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ArticleTypeMap';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const REQUIRED_ARTICLE_TYPES = [
  ArticleType.ACCOMMODATION,
  ArticleType.FLIGHT,
  ArticleType.SERVICE,
  ArticleType.TRAVEL,
  ArticleType.AUTOMOBILE,
];
export const FALLBACK_ARTICLE_TYPES = [ArticleType.ONLINE_PRODUCT];
export const SINGLE_UNIT_ARTICLE_TYPES = [ArticleType.SERVICE, ArticleType.AUTOMOBILE];

export const useArticleTypes = () => {
  const {
    state: { selectedAgent },
  } = useApplicationContext();
  const { cart, setCartRequiredArticleTypes } = useCart();

  const getRequiredArticleTypes = (allowedArticleTypes: ArticleType[] | undefined) => {
    if (!allowedArticleTypes || allowedArticleTypes.length === 0) return [];
    return REQUIRED_ARTICLE_TYPES.filter((item) => allowedArticleTypes.find((type) => type === item)) || [];
  };

  const allowedArticleTypes = selectedAgent?.allowedArticleTypes || FALLBACK_ARTICLE_TYPES;
  const requiredArticleTypes = getRequiredArticleTypes(allowedArticleTypes);
  const hasRequiredArticles = !!requiredArticleTypes?.length;
  const missingRequiredArticleTypes = cart?.missingRequiredArticleTypes();
  const needsRequiredArticle =
    missingRequiredArticleTypes?.length && requiredArticleTypes.length === missingRequiredArticleTypes.length;

  useEffect(() => {
    setCartRequiredArticleTypes(getRequiredArticleTypes(allowedArticleTypes));
  }, [allowedArticleTypes]);

  const getSelectableArticleTypes = (currentItemIndex: number = -1) => {
    if (!allowedArticleTypes?.length) {
      return FALLBACK_ARTICLE_TYPES;
    }

    const selectable = removeSingleUnitArticleTypesIfAlreadyInCart(currentItemIndex);

    if (!selectable.length) {
      return FALLBACK_ARTICLE_TYPES;
    }

    return selectable;
  };

  const removeSingleUnitArticleTypesIfAlreadyInCart = (currentItemIndex: number): ArticleType[] => {
    const selectable = [...allowedArticleTypes];

    for (const articleType of SINGLE_UNIT_ARTICLE_TYPES) {
      const hasType = allowedArticleTypes.includes(articleType);
      if (!hasType) continue;

      const typeAlreadyInCart = !!cart?.items.filter(
        (item, i) => item.data.type === articleType && i !== currentItemIndex,
      ).length;

      if (typeAlreadyInCart) {
        selectable.splice(selectable.indexOf(articleType, 1));
      }
    }

    return selectable;
  };

  const getDefaultFormItem = () => {
    const selectableArticleTypes = getSelectableArticleTypes();
    const newArticleType = !hasRequiredArticles
      ? selectableArticleTypes[0]
      : requiredArticleTypes.filter((item) => selectableArticleTypes.includes(item))[0] || selectableArticleTypes[0];
    return {
      ...DefaultCartItem,
      type: newArticleType,
      category: ArticleTypeMap.toArticleCategories(newArticleType)[0],
    };
  };

  return {
    allowedArticleTypes,
    requiredArticleTypes,
    missingRequiredArticleTypes,
    needsRequiredArticle,
    getSelectableArticleTypes,
    defaultFormItem: getDefaultFormItem() as FormItem | MinimalFormItem,
  };
};
